import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { TimeHlp } from 'src/lib/fliko/helpers/time.helper';
import { UserHlp } from 'src/lib/fliko/helpers/user.helper';
import { CommunityTenancyData } from 'src/lib/fliko/models/agreements/communityTenancy.data';
import { TenancyAgreementData } from 'src/lib/fliko/models/agreements/tenancyAgr.data';
import { EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { ResidentData, ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ApartmentData } from 'src/lib/fliko/models/units/apartment.data';
import { ParkingData } from 'src/lib/fliko/models/units/parking.data';
import { StorageData } from 'src/lib/fliko/models/units/storage.data';
import { RentalUnitType, RentalUnit } from 'src/lib/fliko/models/units/unit.data';
import { ShareData } from 'src/lib/fliko/models/users/share.data';
import { UserData } from 'src/lib/fliko/models/users/user.data';
import { AsyncHlp } from 'src/app/shared/helpers/async.helper';
import { UserHelper } from 'src/app/shared/helpers/user-helper';
import { UnitFilterDictionary } from 'src/app/shared/models/unit-filter-dictionary.model';
import { idMap } from 'src/app/shared/operators/idMap.operator';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { EstateService } from 'src/app/view/estate-view/services/estate.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { AgreementService } from './agreement.service';
import { LocalizationFilters } from './models/users-filters';
import { RentalUnitVM, ResidentUnitsVM, UnitsCollectionVM, ResidentWithType } from './models/users-view-models';
import { ResidentService } from './resident.service';
import { ShareService } from './share.service';
import { UnitService } from './unit.service';

interface UnitMaps {
  apartment: Map<string, ApartmentData>;
  parking: Map<string, ParkingData>;
  storage: Map<string, StorageData>;
  /** Parkings with apartmentId, key is apartmentId */
  connectedParking: Map<string, ParkingData[]>;
  /** Storages with apartmentId, key is apartmentId */
  connectedStorage: Map<string, StorageData[]>;
}

@Injectable({
  providedIn: 'root'
})
export class UsersDataService implements OnDestroy {
  // TODO dictionaries
  private apartmentsFilters$ = new BehaviorSubject(new LocalizationFilters([], this.ls));
  private parkingsFilters$ = new BehaviorSubject(new LocalizationFilters([], this.ls));
  private storagesFilters$ = new BehaviorSubject(new LocalizationFilters([], this.ls));

  private unitFilterDictionary$: BehaviorSubject<UnitFilterDictionary> | null = null;
  private resUnitMap$: BehaviorSubject<Map<string, UnitsCollectionVM>> | null = null;
  private residentUnitsVMs$: BehaviorSubject<ResidentUnitsVM[]> | null = null;
  /** All landlords and tenants (NO UNASSIGNED) */
  private residentsWithType$: BehaviorSubject<ResidentWithType[]> | null = null;

  // FIXME delete this, see getResidentUnitsVMs()to know why
  private residentUnitsVMsWithExTenants$: BehaviorSubject<ResidentUnitsVM[]> | null = null;

  private estate$ = this.ctxDS.currentEstate$;
  private units$ = combineLatest([
    this.unitService.getApartments$(),
    this.unitService.getParkings$(),
    this.unitService.getStorages$()
  ]);
  private unitsMaps$ = this.units$.pipe(
    map(([apartments, parkings, storages]) => {
      const parking = new Map<string, ParkingData>();
      const connectedParking = new Map<string, ParkingData[]>();
      const storage = new Map<string, StorageData>();
      const connectedStorage = new Map<string, StorageData[]>();
      parkings.forEach((par) => {
        parking.set(par.id, par);
        if (par.apartmentId) MapHlp.arrayMapAdd(connectedParking, par.apartmentId, par);
      });
      storages.forEach((sto) => {
        storage.set(sto.id, sto);
        if (sto.apartmentId) MapHlp.arrayMapAdd(connectedStorage, sto.apartmentId, sto);
      });
      return {
        apartment: MapHlp.idMap(apartments),
        parking,
        storage,
        connectedParking,
        connectedStorage
      };
    })
  );
  private subscription$ = new Subscription();

  constructor(
    private unitService: UnitService,
    private residentService: ResidentService,
    private shareService: ShareService,
    private agreementService: AgreementService,
    private ls: LocalizationService,
    private ctxDS: ContextDataService,
    private estateService: EstateService,
    @Optional() @SkipSelf() thisService?: UsersDataService
  ) {
    if (thisService) throw new Error('Multiple instances of UsersDataService');
    this.initializeFilters();
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public getLocalizationFilters$(type: RentalUnitType | EstateType.PRS): Observable<LocalizationFilters> {
    switch (type) {
      case EstateType.PRS:
      case RentalUnitType.APARTMENT:
        return this.apartmentsFilters$.asObservable();
      case RentalUnitType.PARKING:
        return this.parkingsFilters$.asObservable();
      case RentalUnitType.STORAGE:
        return this.storagesFilters$.asObservable();
    }
  }

  public getResidentsUnitsMap$(): Observable<Map<string, UnitsCollectionVM>> {
    if (!this.resUnitMap$) {
      this.resUnitMap$ = new BehaviorSubject(new Map());
      this.subscription$.add(
        combineLatest([
          this.estate$,
          this.shareService.getShares$(),
          this.agreementService.getCommAgreement$(),
          this.agreementService.getPrsAgreements$(),
          this.unitsMaps$
        ]).subscribe(([estate, shares, tenancy, agreements, maps]) => {
          if (estate) {
            let mapVal = new Map<string, UnitsCollectionVM>();
            if (estate.type == EstateType.COMMUNITY) {
              mapVal = this.prepareCommunityResidentUnitMap(shares, tenancy, maps);
            } else {
              mapVal = this.preparePrsResidentUnitMap(agreements, maps);
            }
            AsyncHlp.updateBS(this.resUnitMap$, mapVal);
          }
        })
      );
    }
    return this.resUnitMap$.asObservable();
  }

  public getUnitFilterDictionary$(): Observable<UnitFilterDictionary> {
    if (!this.unitFilterDictionary$) {
      this.unitFilterDictionary$ = new BehaviorSubject<UnitFilterDictionary>(UnitFilterDictionary.empty());

      this.subscription$.add(
        this.estateService.getLocationDictionary$().subscribe((dictionary) => {
          const unitFilterDict = UnitFilterDictionary.empty();
          const upper = (x: string) => x.toLocaleUpperCase();
          const addFilter = (type: keyof UnitFilterDictionary, ...values: (string | null)[]): void => {
            const sValues = values.map((x) => x ?? '-');
            unitFilterDict[type].push({ title: upper(sValues.join('/')), value: sValues.join('|') });
          };

          dictionary?.buildings.forEach((building) => {
            addFilter('buildings', building.number);

            building.staircases?.forEach((staircase) => {
              addFilter('staircases', building.number, staircase.number);
              const addNumber = (number: string | null) => addFilter('numbers', building.number, staircase.number, number);

              staircase.apartments?.forEach((ap) => addNumber(ap.number));
              staircase.parkings?.forEach((par) => addNumber(par.number));
              staircase.storages?.forEach((sto) => addNumber(sto.number));
            });
          });

          AsyncHlp.updateBS(this.unitFilterDictionary$, unitFilterDict);
        })
      );
    }
    return this.unitFilterDictionary$.asObservable();
  }

  private residentUnitsSources$ = combineLatest([
    this.residentService.getResidents$().pipe(idMap()),
    this.shareService.getShares$(),
    this.agreementService.getCommAgreement$(),
    this.agreementService.getPrsAgreements$(),
    this.unitsMaps$,
    this.ctxDS.usersInEstate$.pipe(idMap())
  ]);

  // FIXME: delete this
  /**
   * @deprecated this functions returns landlords, tenants and wrongly ex-tenants as tenants,
   * should be replaced by getResUnitsVMs$() everywhere, after ensuring that this change do not break existing functionality
   */
  public getResidentUnitsVMs$(): Observable<ResidentUnitsVM[]> {
    if (!this.residentUnitsVMsWithExTenants$) {
      this.residentUnitsVMsWithExTenants$ = new BehaviorSubject<ResidentUnitsVM[]>([]);
      this.subscription$.add(
        this.residentUnitsSources$.subscribe(([residentsMap, shares, tenancy, agreements, maps, usersMap]) => {
          const bsData = this.prepareResidentUnitsVMs(residentsMap, maps, shares, tenancy, agreements, usersMap, true);
          AsyncHlp.updateBS(this.residentUnitsVMsWithExTenants$, bsData);
        })
      );
    }
    return this.residentUnitsVMsWithExTenants$.asObservable();
  }

  public getResUnitsVMs$(): Observable<ResidentUnitsVM[]> {
    if (!this.residentUnitsVMs$) {
      this.residentUnitsVMs$ = new BehaviorSubject<ResidentUnitsVM[]>([]);
      this.subscription$.add(
        this.residentUnitsSources$.subscribe(([residentsMap, shares, tenancy, agreements, maps, usersMap]) => {
          const bsData = this.prepareResidentUnitsVMs(residentsMap, maps, shares, tenancy, agreements, usersMap, false);
          AsyncHlp.updateBS(this.residentUnitsVMs$, bsData);
        })
      );
    }
    return this.residentUnitsVMs$.asObservable();
  }

  public getResidentsWithType$(): Observable<ResidentWithType[]> {
    if (!this.residentsWithType$) {
      this.residentsWithType$ = new BehaviorSubject<ResidentWithType[]>([]);
      this.subscription$.add(
        combineLatest([
          this.residentService.getResidents$(),
          this.shareService.getShares$(),
          this.agreementService.getCommAgreement$(),
          this.agreementService.getPrsAgreements$()
        ]).subscribe(([residents, shares, tenancies, agreements]) => {
          const resWithTypeMap = new Map<string, ResidentWithType>();

          const addResident = (res: ResidentData, residentType: ResidentType) =>
            resWithTypeMap.set(`${res.id}|${residentType}`, { ...res, residentType });

          const landlordIds = new Set<string>();
          const tenantsIds = new Set<string>();

          shares.forEach((share) => {
            landlordIds.add(share.ownerId);
            if (share.coOwnerId) landlordIds.add(share.coOwnerId);
          });

          tenancies.forEach((tenancy) => tenantsIds.add(tenancy.tenantId));

          agreements.forEach((agr) => {
            agr.landlords.forEach((l) => landlordIds.add(l.id));
            agr.tenants?.forEach((t) => tenantsIds.add(t.id));
          });

          residents.forEach((res) => {
            if (landlordIds.has(res.id)) addResident(res, ResidentType.LANDLORD);
            if (tenantsIds.has(res.id)) addResident(res, ResidentType.TENANT);
          });

          AsyncHlp.updateBS(this.residentsWithType$, Array.from(resWithTypeMap.values()));
        })
      );
    }
    return this.residentsWithType$.asObservable();
  }

  private initializeFilters(): void {
    this.subscription$.add(
      combineLatest([this.estate$, this.units$]).subscribe(([estate, [apartments, parkings, storages]]) => {
        if (estate) {
          if (estate.type == EstateType.COMMUNITY) {
            this.apartmentsFilters$.next(new LocalizationFilters(apartments, this.ls));
            this.parkingsFilters$.next(new LocalizationFilters(parkings, this.ls));
            this.storagesFilters$.next(new LocalizationFilters(storages, this.ls));
          } else {
            this.apartmentsFilters$.next(new LocalizationFilters([...apartments, ...parkings, ...storages], this.ls));
          }
        }
      })
    );
  }

  private prepareCommunityResidentUnitMap(
    shares: ShareData[],
    tenancies: CommunityTenancyData[],
    units: UnitMaps
  ): Map<string, UnitsCollectionVM> {
    const resUnitMap = new Map<string, UnitsCollectionVM>();
    const aptResMap = new Map<string, string[]>();

    shares.forEach((share) => {
      const unit = units[share.unitType].get(share.unitId);

      if (unit) {
        this.addUnit(resUnitMap, share.ownerId, unit, share.unitType);
        if (share.coOwnerId) this.addUnit(resUnitMap, share.coOwnerId, unit, share.unitType);

        if (share.unitType == RentalUnitType.APARTMENT) {
          MapHlp.arrayMapAdd(aptResMap, unit.id, share.ownerId);
          if (share.coOwnerId) MapHlp.arrayMapAdd(aptResMap, unit.id, share.coOwnerId);
        }
      }
    });

    tenancies.forEach((tenancy) => {
      if (!tenancy.units) {
        console.warn(`No units in tenancy ${tenancy.id}`);
        return;
      }
      tenancy.units.forEach((tUnit) => {
        const unit = units[tUnit.type].get(tUnit.id);
        if (unit) {
          this.addUnit(resUnitMap, tenancy.tenantId, unit, tUnit.type);

          if (tUnit.type == RentalUnitType.APARTMENT) MapHlp.arrayMapAdd(aptResMap, tUnit.id, tenancy.tenantId);
        }
      });
    });

    units.parking.forEach((parking) => {
      if (parking.apartmentId) {
        aptResMap.get(parking.apartmentId)?.forEach((resId) => this.addUnit(resUnitMap, resId, parking, RentalUnitType.PARKING));
      }
    });

    units.storage.forEach((storage) => {
      if (storage.apartmentId) {
        aptResMap.get(storage.apartmentId)?.forEach((resId) => this.addUnit(resUnitMap, resId, storage, RentalUnitType.STORAGE));
      }
    });

    return resUnitMap;
  }

  private preparePrsResidentUnitMap(agreements: TenancyAgreementData[], units: UnitMaps): Map<string, UnitsCollectionVM> {
    const resUnitMap = new Map<string, UnitsCollectionVM>();
    const now = Date.now();

    for (const agreement of agreements) {
      if (!TimeHlp.inRange(now, agreement.agreementStartDate, agreement.agreementEndDate)) continue;

      agreement.landlords.forEach((landlord) => {
        agreement.tenancyApartmentDetails?.forEach((tad) => {
          const apartment = units.apartment.get(tad.apartmentId);
          if (apartment) this.addUnit(resUnitMap, landlord.id, apartment, RentalUnitType.APARTMENT);
        });
        agreement.tenancyParkingSpotDetails?.forEach((tpd) => {
          const parking = units.apartment.get(tpd.parkingSpotId);
          if (parking) this.addUnit(resUnitMap, landlord.id, parking, RentalUnitType.PARKING);
        });
        agreement.tenancyStorageDetails?.forEach((tsd) => {
          const storage = units.apartment.get(tsd.storageId);
          if (storage) this.addUnit(resUnitMap, landlord.id, storage, RentalUnitType.STORAGE);
        });
      });

      agreement.tenants?.forEach((tenant) => {
        const apartment = units.apartment.get(tenant.apartmentId);
        if (apartment) this.addUnit(resUnitMap, tenant.id, apartment, RentalUnitType.APARTMENT);

        const parking = tenant.parkingSpot ? units.apartment.get(tenant.parkingSpot) : null;
        if (parking) this.addUnit(resUnitMap, tenant.id, parking, RentalUnitType.PARKING);

        const storage = tenant.storage ? units.apartment.get(tenant.storage) : null;
        if (storage) this.addUnit(resUnitMap, tenant.id, storage, RentalUnitType.STORAGE);
      });
    }

    return resUnitMap;
  }

  private prepareResidentUnitsVMs(
    residentsMap: Map<string, ResidentData>,
    unitMaps: UnitMaps,
    shares: ShareData[],
    tenancies: CommunityTenancyData[],
    agreements: TenancyAgreementData[],
    usersMap: Map<string, UserData>,
    includeExTenants: boolean
  ): ResidentUnitsVM[] {
    const residentVMsMap = new Map<string, ResidentUnitsVM>();

    const updateVMMap = (residentId: string, type: ResidentType, units: RentalUnitVM[]) => {
      const residentVM = residentVMsMap.get(`${residentId}|${type}`);
      if (!residentVM) {
        const res = residentsMap.get(residentId);
        const addresses = units.map((x) =>
          `&${x.unit.blockNumber ?? '-'}|${(x.unit as any).staircaseNumber ?? '-'}|${x.unit.number ?? '-'}&`.toLowerCase()
        );
        if (res) {
          const user = usersMap.get(res.userId ?? '') ?? null;
          residentVMsMap.set(`${residentId}|${type}`, { resident: res, type, units, addresses, user });
        }
      } else {
        residentVM.units.push(...units);
      }
    };

    shares.forEach((share) => {
      const unit = unitMaps[share.unitType].get(share.unitId);
      if (!unit) return;
      const units: RentalUnitVM[] = [UserHelper.unitToRentalUnitVM(unit, share.unitType)];
      if (share.unitType == RentalUnitType.APARTMENT) {
        const parkings = unitMaps.connectedParking.get(share.unitId) ?? [];
        const storages = unitMaps.connectedStorage.get(share.unitId) ?? [];
        units.push(
          ...parkings.map((x) => UserHelper.unitToRentalUnitVM(x, RentalUnitType.PARKING)),
          ...storages.map((x) => UserHelper.unitToRentalUnitVM(x, RentalUnitType.STORAGE))
        );
      }

      updateVMMap(share.ownerId, ResidentType.LANDLORD, units);
      if (share.coOwnerId) updateVMMap(share.coOwnerId, ResidentType.LANDLORD, units);
    });

    tenancies.forEach((tenancy) => {
      const unitVMs: RentalUnitVM[] = [];
      // FIXME: somehow, there is tenancy without units but i don't know if it's staging-only problem
      if (!tenancy.units) {
        console.warn(`No units in tenancy ${tenancy.id}`);
        return;
      }
      // HACK remove includeExTenants when not needed
      if (includeExTenants || UserHlp.isCurrentCommunityTenancy(tenancy)) {
        tenancy.units.forEach((u) => {
          const unit = unitMaps[u.type].get(u.id);
          if (unit) unitVMs.push(UserHelper.unitToRentalUnitVM(unit, u.type));
        });
        updateVMMap(tenancy.tenantId, ResidentType.TENANT, unitVMs);
      }
    });

    const agrToUnitVM = (type: RentalUnitType, ids: string[]): RentalUnitVM[] => {
      const units: RentalUnitVM[] = [];
      ids.forEach((id) => {
        const u = unitMaps[type].get(id);
        if (u) units.push(UserHelper.unitToRentalUnitVM(u, type));
      });
      return units;
    };
    agreements.forEach((agr) => {
      // HACK remove includeExTenants when not needed
      if (includeExTenants || UserHlp.isCurrentAgreement(agr)) {
        const units = [
          ...agrToUnitVM(RentalUnitType.APARTMENT, agr.tenancyApartmentDetails?.map((x) => x.apartmentId) ?? []),
          ...agrToUnitVM(RentalUnitType.PARKING, agr.tenancyParkingSpotDetails?.map((x) => x.parkingSpotId) ?? []),
          ...agrToUnitVM(RentalUnitType.STORAGE, agr.tenancyStorageDetails?.map((x) => x.storageId) ?? [])
        ];
        agr.landlords.forEach((l) => updateVMMap(l.id, ResidentType.LANDLORD, units));
        agr.tenants?.forEach((t) =>
          updateVMMap(
            t.id,
            ResidentType.TENANT,
            units.filter((u) => u.id == t.apartmentId || u.id == t.parkingSpot || u.id == t.storage)
          )
        );
      }
    });

    return Array.from(residentVMsMap.values());
  }

  private addUnit(map: Map<string, UnitsCollectionVM>, key: string, unit: RentalUnit, type: RentalUnitType): void {
    if (!map.has(key)) {
      map.set(key, {
        all: [],
        apartment: [],
        parking: [],
        storage: []
      });
    }
    const val = map.get(key)!;
    // @ts-expect-error Dynamic enum key selects a matching typed bucket
    val[type].push(unit);
    val.all.push(unit);
  }
}
